/**
 * Org-page extension — list of applications directly owned by this
 * organisation. Provided by the applications plugin via the extension
 * registry; renders into `org.detail.main.bottom`.
 *
 * Scope decision: this card shows ONLY apps directly owned by THIS org,
 * not descendants. Cascading rolls up through the tree separately (apps
 * page filters by visibility). If you want to see what an MSP root org's
 * subtree contains, drill into the child orgs.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ServerCog, Loader2, ChevronRight, Sparkles } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { api, type Application } from '@/api/client.js';
import type { OrgDetailContext } from '../types.js';
import { resolveCapabilities } from '@/lib/templates.js';

export function OrgApplicationsCard({ orgId, orgSlug }: OrgDetailContext): JSX.Element {
  const query = useQuery({
    queryKey: ['applications', 'for-org', orgId],
    queryFn: () => api.applications.listForOrg(orgId),
    enabled: !!orgId,
  });
  const apps = query.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ServerCog className="h-4 w-4" />
          Applications
        </CardTitle>
        <CardDescription>
          Applications directly owned by this organisation. Apps owned by descendant
          organisations show up on those orgs&apos; pages.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : apps.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/70 bg-muted/30 px-4 py-6 text-center text-xs text-muted-foreground">
            No applications yet.{' '}
            <Link
              to="/p/applications"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Create one →
            </Link>
          </p>
        ) : (
          <div className="divide-y divide-border rounded-md border border-border">
            {apps.map((app: Application) => {
              const caps = resolveCapabilities(app.templateId);
              return (
                <Link
                  key={app.id}
                  to="/p/applications/applications/$slug"
                  params={{ slug: app.slug }}
                  search={{ org: orgSlug }}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm transition-colors hover:bg-accent/40"
                >
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      {caps.scripts && <Sparkles className="h-3 w-3 text-primary/70" />}
                      <span className="truncate font-medium">{app.name}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        /{app.slug}
                      </span>
                    </div>
                    {app.description && (
                      <p className="truncate text-[11px] text-muted-foreground">
                        {app.description}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
                    {app.templateId && (
                      <span className="rounded-md border border-border bg-muted/30 px-1.5 py-0.5 font-mono">
                        {app.templateId}
                      </span>
                    )}
                    <ChevronRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
